import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

/**
 * Unit 개별 가격 — 로컬 BE candidate(010, 미배포) OpenAPI·fe-answers 기준이다.
 * GET ?view=edit이 준 cas를 한 글자도 바꾸지 않고 PUT expected로 되돌려 보내며(stale·추가·누락은 서버가 409로 거절),
 * 쓰기 결과는 같은 command의 응답과 다시 읽은 현재 가격으로만 확정한다. NULL은 상속, 0은 명시 면제다.
 */
export interface UnitPricingValues {
  regular_amount_minor: number | null;
  deposit_amount: number | null;
  cleaning_fee: number | null;
}
export interface UnitPricingFieldSource {
  value: number | null;
  source_kind?: string | null;
  source_policy_id: string | null;
  revision: number | null;
  reason: string | null;
}
export interface UnitPolicyData extends UnitPricingValues {
  id: string;
  target_kind: 'UNIT';
  unit_id: string;
  revision: number;
  deleted_at: string | null;
  value_semantics: 'NATIVE_EXPLICIT' | 'LEGACY_PRESERVED';
  [key: string]: unknown;
}
export interface UnitPricingEdit {
  target: {
    kind: 'UNIT';
    property_id: string;
    type_id: string;
    room_id: string;
    unit_id: string;
    structure_revision: number;
    version: number;
  };
  type_policy: Record<string, unknown> | null;
  room_policy: Record<string, unknown> | null;
  unit_policy: UnitPolicyData | null;
  rules: { type: unknown[]; room: unknown[]; unit: unknown[] };
  tiers: unknown[];
  cas: Record<string, unknown>;
  effective_fields: Record<keyof UnitPricingValues, UnitPricingFieldSource>;
}
export interface UnitPricingCommandResult {
  command_id: string;
  outcome: 'CHANGED' | 'NO_CHANGE';
  /** OpenAPI는 두 값을 nullable로 선언한다(NO_CHANGE 등). 확정은 다시 읽은 현재 가격으로 한다. */
  policy_id: string | null;
  result_revision: number | null;
}
export interface UnitPricingInput {
  command_id: string;
  expected: Record<string, unknown>;
  values: UnitPricingValues;
  reason: string;
}

type Obj = Record<string, unknown>;
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '슬롯 가격 계약을 확인하지 못했습니다.',
  });
const obj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const closed = (v: unknown, keys: string): v is Obj =>
  obj(v) && Object.keys(v).sort().join(',') === keys.split(' ').sort().join(',');
const integer = (v: unknown, min = 0): v is number => Number.isSafeInteger(v) && Number(v) >= min;
const uuid = (v: unknown): v is string => typeof v === 'string' && isUuid(v);
const same = (a: unknown, b: string) => uuid(a) && a.toLowerCase() === b.toLowerCase();
const fieldKeys = ['regular_amount_minor', 'deposit_amount', 'cleaning_fee'] as const;

const token = (v: unknown) =>
  closed(v, 'id revision deleted_at') &&
  (v.id === null || uuid(v.id)) &&
  integer(v.revision) &&
  (v.deleted_at === null || isOffsetDateTime(v.deleted_at)) &&
  (v.id === null ? v.revision === 0 && v.deleted_at === null : v.revision > 0);
const targetValid = (v: unknown, unitId: string): v is UnitPricingEdit['target'] =>
  closed(v, 'kind property_id type_id room_id unit_id structure_revision version') &&
  v.kind === 'UNIT' &&
  uuid(v.property_id) &&
  uuid(v.type_id) &&
  uuid(v.room_id) &&
  same(v.unit_id, unitId) &&
  integer(v.structure_revision, 1) &&
  integer(v.version, 1);
/** 서버가 준 cas는 불투명 비교 자료다 — 구조만 확인하고 값은 바꾸지 않는다. */
const casValid = (v: unknown, unitId: string, target: UnitPricingEdit['target']): v is Obj =>
  closed(v, 'property_id target policies tiers rule_revisions') &&
  same(v.property_id, target.property_id) &&
  targetValid(v.target, unitId) &&
  Object.keys(target).every((key) => (v.target as Obj)[key] === (target as unknown as Obj)[key]) &&
  closed(v.policies, 'TYPE ROOM UNIT') &&
  Object.values(v.policies).every(token) &&
  closed(v.tiers, 'S M L') &&
  obj(v.rule_revisions);
const policyValid = (v: unknown, unitId: string): boolean =>
  v === null ||
  (obj(v) &&
    uuid(v.id) &&
    v.target_kind === 'UNIT' &&
    same(v.unit_id, unitId) &&
    integer(v.revision, 1) &&
    (v.deleted_at === null || isOffsetDateTime(v.deleted_at)) &&
    ['NATIVE_EXPLICIT', 'LEGACY_PRESERVED'].includes(String(v.value_semantics)) &&
    fieldKeys.every((key) => v[key] === null || integer(v[key])));

export function decodeUnitPricingEdit(v: unknown, unitId: string): UnitPricingEdit {
  if (
    !closed(v, 'target type_policy room_policy unit_policy rules tiers cas effective_fields') ||
    !targetValid(v.target, unitId) ||
    !casValid(v.cas, unitId, v.target) ||
    !policyValid(v.unit_policy, unitId) ||
    !(v.type_policy === null || obj(v.type_policy)) ||
    !(v.room_policy === null || obj(v.room_policy)) ||
    !closed(v.rules, 'type room unit') ||
    ![v.rules.type, v.rules.room, v.rules.unit].every(Array.isArray) ||
    !Array.isArray(v.tiers) ||
    !closed(v.effective_fields, fieldKeys.join(' ')) ||
    !Object.values(v.effective_fields).every(
      (field) =>
        obj(field) &&
        (field.value === null || integer(field.value)) &&
        (field.source_policy_id === null || uuid(field.source_policy_id)) &&
        (field.revision === null || integer(field.revision)) &&
        (field.reason === null || typeof field.reason === 'string'),
    )
  )
    throw invalid();
  return v as unknown as UnitPricingEdit;
}

export async function readUnitPricingEdit(
  client: ServiceClient,
  unitId: string,
  signal?: AbortSignal,
): Promise<UnitPricingEdit> {
  if (!isUuid(unitId)) throw invalid();
  const data = await client.get<unknown>(`admin/units/${unitId}/pricing`, {
    params: { view: 'edit' },
    headers: { 'Cache-Control': 'no-store' },
    signal,
  });
  return decodeUnitPricingEdit(data, unitId);
}

export function validateUnitPricingInput(unitId: string, input: UnitPricingInput): void {
  if (
    !isUuid(unitId) ||
    !closed(input, 'command_id expected values reason') ||
    !uuid(input.command_id) ||
    typeof input.reason !== 'string' ||
    !input.reason.trim() ||
    Array.from(input.reason).length > 500 ||
    !closed(input.values, fieldKeys.join(' ')) ||
    !fieldKeys.every((key) => {
      const value = input.values[key];
      return value === null || integer(value, key === 'regular_amount_minor' ? 1 : 0);
    }) ||
    !obj(input.expected)
  )
    throw invalid();
}

/** One explicit command only. 결과 불명이어도 자동 재전송하지 않는다. */
export async function setUnitPricing(
  client: ServiceClient,
  unitId: string,
  input: UnitPricingInput,
): Promise<UnitPricingCommandResult> {
  validateUnitPricingInput(unitId, input);
  const v = await client.put<unknown>(`admin/units/${unitId}/pricing`, { json: input });
  if (
    !closed(
      v,
      'command_id outcome target_id policy_id rule_id result_revision first_result current_snapshot_at_first_execution',
    ) ||
    !same(v.command_id, input.command_id) ||
    !['CHANGED', 'NO_CHANGE'].includes(String(v.outcome)) ||
    !(v.policy_id === null || uuid(v.policy_id)) ||
    v.rule_id !== null ||
    !(v.result_revision === null || integer(v.result_revision, 1)) ||
    !closed(v.first_result, 'schema_version request_fingerprint received_at') ||
    v.first_result.schema_version !== 1 ||
    !obj(v.current_snapshot_at_first_execution) ||
    !targetValid(v.current_snapshot_at_first_execution.target, unitId)
  )
    throw invalid();
  return v as unknown as UnitPricingCommandResult;
}
